import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Swords, Search } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { BattleCard } from "@/components/Battles";

const PAGE = 30;
const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};
const yr = (y) => (y < 0 ? `${-y} BC` : String(y));

export default function Battles() {
  const { era: eraSlug } = useParams();
  const [summary, setSummary] = useState(null);
  const eraStat = summary?.eras.find((e) => e.slug === eraSlug) || null;
  const era = eraStat?.era || "";
  const [allEras, setAllEras] = useState(false);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);

  useEffect(() => { api.get("/battles/summary").then((r) => setSummary(r.data)).catch(() => setSummary({ total: 0, eras: [], regions: [] })); }, []);
  useEffect(() => { setSkip(0); }, [eraSlug]);
  useEffect(() => {
    if (eraSlug && !summary) return;
    if (eraSlug && !eraStat) { setData({ total: 0, items: [] }); return; }
    api.get("/battles", { params: { era: era || undefined, q: query || undefined, limit: PAGE, skip } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [] }));
  }, [era, eraSlug, eraStat, summary, query, skip]);
  useEffect(() => {
    document.title = era ? `${era} battles — ${eraStat.count} battles, ${yr(eraStat.from)}–${yr(eraStat.to)} | OddlyTrue` : `Historic Battles — ${summary ? summary.total.toLocaleString() + " " : ""}real battles through history | OddlyTrue`;
    setMeta("description", era ? `Every ${era} battle in the OddlyTrue almanac: ${eraStat.count} battles from ${yr(eraStat.from)} to ${yr(eraStat.to)} — who fought whom, the result, the cost, and where it happened on the map.` : "Real battles through history, day by day — belligerents, results, casualties and a map for each.");
    setCanonical(`${window.location.origin}/battles${eraSlug ? `/${eraSlug}` : ""}`);
    setMeta("robots", eraSlug && summary && !eraStat ? "noindex, follow" : "index, follow");
    document.querySelectorAll("script[data-battles-ld]").forEach((el) => el.remove());
    if (era && data.items.length && skip === 0 && !query) {
      const ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.dataset.battlesLd = "1";
      ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "ItemList", name: document.title, itemListElement: data.items.map((b, i) => ({ "@type": "ListItem", position: i + 1, item: { "@type": "Event", name: b.battle, startDate: b.year > 0 ? `${String(b.year).padStart(4, "0")}-${b.date_dd_mm.slice(3)}-${b.date_dd_mm.slice(0, 2)}` : undefined, location: b.location ? { "@type": "Place", name: b.location, geo: b.lat != null ? { "@type": "GeoCoordinates", latitude: b.lat, longitude: b.lon } : undefined } : undefined, sameAs: b.wiki_url || undefined } })) });
      document.head.appendChild(ld);
    }
    return () => document.querySelectorAll("script[data-battles-ld]").forEach((el) => el.remove());
  }, [era, eraSlug, eraStat, summary, data, skip, query]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE));
  const page = Math.floor(skip / PAGE) + 1;

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="battles-page">
        <div className="flex items-center gap-2">
          <Swords className="w-4 h-4 text-gold" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">{eraSlug ? <><Link to="/battles" className="hover:underline">Historic Battles</Link> / {era || eraSlug}</> : "Historic Battles"}</p>
        </div>
        <h1 data-testid="battles-h1" className="mt-2 text-4xl sm:text-5xl font-bold text-ink leading-tight">{era ? `${era} battles` : eraSlug && summary ? "Era not found" : `${summary ? summary.total.toLocaleString() : ""} real battles through history`}</h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">
          {era ? <>{eraStat.count} battles from {yr(eraStat.from)} to {yr(eraStat.to)} — who fought whom, what it decided, the cost, and where it happened on the map.</>
            : <>From Muye (1046 BC) to the last Luftwaffe offensive — who fought whom, what it decided, the cost, and where it happened on the map. {summary && <span className="text-ink">{summary.total.toLocaleString()} battles across {summary.eras.length} eras.</span>}</>}
        </p>

        <form onSubmit={(e) => { e.preventDefault(); setSkip(0); setQuery(q.trim()); }} className="mt-6 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input data-testid="battles-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search battle, place or belligerent" className="w-full h-11 pl-9 pr-3 rounded-full border border-[#e5e7eb] bg-white text-sm text-ink focus:border-gold outline-none" />
          </div>
          <button type="submit" data-testid="battles-search-submit" className="px-5 h-11 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">Search</button>
        </form>

        {summary && (
          <nav aria-label="Eras" className="mt-4 flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible" data-testid="battles-eras">
            <Link to="/battles" data-testid="battles-era-all" className={`shrink-0 px-3 h-8 inline-flex items-center rounded-full text-[12px] font-semibold border ${!era ? "bg-ink text-white border-ink" : "bg-white text-ink border-[#e5e7eb] hover:border-gold"}`}>All eras</Link>
            {[...summary.eras].sort((a, b) => b.count - a.count).filter((e) => allEras || e.count >= 8 || e.era === era).map((e) => (
              <Link key={e.era} to={`/battles/${e.slug}`} data-testid={`battles-era-${e.slug}`} className={`shrink-0 px-3 h-8 inline-flex items-center rounded-full text-[12px] font-semibold border ${era === e.era ? "bg-ink text-white border-ink" : "bg-white text-ink border-[#e5e7eb] hover:border-gold"}`}>{e.era} <span className="ml-1 opacity-60">{e.count}</span></Link>
            ))}
            {summary.eras.some((e) => e.count < 8) && <button type="button" data-testid="battles-eras-toggle" onClick={() => setAllEras(!allEras)} className="shrink-0 px-3 h-8 rounded-full text-[12px] font-semibold text-gold hover:underline">{allEras ? "Fewer eras" : `More eras (${summary.eras.filter((e) => e.count < 8).length})`}</button>}
          </nav>
        )}

        <p data-testid="battles-total" className="mt-6 text-[12px] text-ink-muted">{data.total} battle{data.total === 1 ? "" : "s"}{era ? ` · ${era}` : ""}{query ? ` · "${query}"` : ""}</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="battles-list">
          {data.items.map((b, i) => <BattleCard key={b.id} b={b} testid={`battles-item-${i + 1}`} />)}
        </div>
        {data.total === 0 && <p data-testid="battles-empty" className="mt-6 text-sm text-ink-muted">No battles match.</p>}

        {pages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button type="button" data-testid="battles-prev" disabled={page <= 1} onClick={() => setSkip(skip - PAGE)} className="px-4 h-10 rounded-full border border-[#e5e7eb] bg-white text-sm font-semibold text-ink disabled:opacity-40">← Prev</button>
            <span className="text-[13px] text-ink-muted">Page {page} of {pages}</span>
            <button type="button" data-testid="battles-next" disabled={page >= pages} onClick={() => setSkip(skip + PAGE)} className="px-4 h-10 rounded-full border border-[#e5e7eb] bg-white text-sm font-semibold text-ink disabled:opacity-40">Next →</button>
          </div>
        )}
        {era && summary && (
          <nav aria-label="Other eras" data-testid="battles-era-footer" className="mt-10 border-t border-ink/10 pt-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">More eras</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {[...summary.eras].sort((a, b) => a.from - b.from).filter((e) => e.era !== era).map((e) => <Link key={e.slug} to={`/battles/${e.slug}`} className="text-sm font-semibold text-ink hover:text-gold">{e.era} battles ({e.count}) →</Link>)}
            </div>
          </nav>
        )}
        <AdSlot id="adsense-battles" slot="MID" size="rect300" className="mt-8" />
      </main>
      <Footer />
    </div>
  );
}
